import { useQuery } from "react-query";
import http from "shared/http";

const PBI_API_BASE = "https://api.powerbi.com/v1.0/myorg/groups/";

export interface PowerBIEmbedRequest {
  workspaceId: string;
  reportId: string;
  /** When provided, skips the GET /reports call — the embed URL is constructed directly. */
  datasetId?: string | null;
  requiresEffectiveIdentity?: boolean;
  agencyCode?: string | null;
  /** RLS role names to apply when requiresEffectiveIdentity is true. */
  roles?: string[];
}

export interface PowerBIEmbedConfig {
  embedToken: string;
  embedUrl: string;
  reportId: string;
  expiry: string | null; // ISO date string from PBI GenerateToken response
}

// Internal helpers
const getAccessToken = async (): Promise<string> => {
  return http.fetchAsync("powerbi/access-token");
};

const pbiGet = async <T>(accessToken: string, url: string): Promise<T> => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok)
    throw new Error(`Power BI API error: ${response.status} ${url}`);
  return response.json();
};

const pbiPost = async <T>(
  accessToken: string,
  url: string,
  body: object,
): Promise<T> => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok)
    throw new Error(`Power BI API error: ${response.status} ${url}`);
  return response.json();
};

// Fetch embed config (access token -> embed URL -> embed token)
const fetchEmbedConfig = async (
  request: PowerBIEmbedRequest,
): Promise<PowerBIEmbedConfig> => {
  const accessToken = await getAccessToken();

  // 1. Resolve embed URL and dataset ID.
  //    When datasetId is already stored on the resource we can skip the GET call;
  //    the embed URL follows the documented stable format.
  let embedUrl: string;
  let resolvedDatasetId: string;

  if (request.datasetId) {
    resolvedDatasetId = request.datasetId;
    embedUrl = `https://app.powerbi.com/reportEmbed?reportId=${request.reportId}&groupId=${request.workspaceId}`;
  } else {
    const reportData = await pbiGet<{ embedUrl: string; datasetId: string }>(
      accessToken,
      `${PBI_API_BASE}${request.workspaceId}/reports/${request.reportId}`,
    );
    resolvedDatasetId = reportData.datasetId;
    embedUrl = reportData.embedUrl;
  }

  // 2. Generate embed token
  const tokenBody: object =
    request.requiresEffectiveIdentity && request.agencyCode
      ? {
          accessLevel: "View",
          allowSaveAs: false,
          identities: [
            {
              username: request.agencyCode,
              roles: request.roles ?? [],
              datasets: [resolvedDatasetId],
              reports: [request.reportId],
            },
          ],
        }
      : { accessLevel: "View", allowSaveAs: false };

  const tokenData = await pbiPost<{ token: string; expiration: string }>(
    accessToken,
    `${PBI_API_BASE}${request.workspaceId}/reports/${request.reportId}/GenerateToken`,
    tokenBody,
  );

  return {
    embedToken: tokenData.token,
    embedUrl,
    reportId: request.reportId,
    expiry: tokenData.expiration ?? null,
  };
};

// React Query hook
/**
 * Fetches a Power BI embed config for the given report.
 * The backend provides only an AD access token; this hook calls the
 * Power BI REST API directly to resolve the embed URL and embed token.
 * Re-fetches automatically when `agencyCode` changes (effective identity / RLS).
 */
export const useGetPowerBIEmbedConfigQuery = (
  request: PowerBIEmbedRequest | null,
) => {
  return useQuery(
    [
      "powerbi-embed",
      request?.workspaceId,
      request?.reportId,
      request?.agencyCode ?? null,
      // Include roles so a different effective-identity/RLS combination gets its own cached token
      ...(request?.roles ?? []),
    ],
    () => fetchEmbedConfig(request!),
    {
      // Do not fetch when effective identity is required but no agency has been
      // selected yet — Power BI will reject a GenerateToken call that is missing
      // a username in the identity object.
      enabled: !!(
        request?.workspaceId &&
        request?.reportId &&
        (!request?.requiresEffectiveIdentity || request?.agencyCode)
      ),
      staleTime: 50 * 60 * 1000, // 50 min — embed token lasts ~1 hr
      cacheTime: 55 * 60 * 1000,
      retry: 1,
    },
  );
};
